import { useEffect, useState } from 'react';
import { RecordModalProps } from '../RecordModal/RecordModal';
import { Device } from '../../../../../../../../types/Device';
import { Button, message, Modal } from 'antd';
import useRecordingStore from '../../../api/recording/recordingStore';
import { ReactComponent as SvgClose } from 'utils/app/assets/icons/Close.svg';
import { ModalCameraGrid } from '../../ModalCameraGrid/ModalCameraGrid';
import './styles.css';
import SelectChecker from '../../../../../../../../utils/shared/components/Select/SelectChecker/SelectChecker';
import { useSelectedLayout } from '../../../../../../../../store/useSelectedLayout';

interface StopRecordingModalProps {
    visible: boolean;
    onOk: (selectedDevices: Device[]) => void;
    onCancel: () => void;
    title: string;
    type: 'audio' | 'video' | 'photo';
    setIsModalVisible: (visible: boolean) => void;
    devices: Device[];
    layoutViewType: string;
}

export const StopRecordingModal: React.FC<StopRecordingModalProps> = ({
    visible,
    onOk,
    onCancel,
    title,
    type,
    setIsModalVisible,
    devices,
}) => {
    const [selectedDevices, setSelectedDevices] = useState<Device[]>([]);
    const { stopRecording, getDevicesWithRecording } = useRecordingStore();
    const { selectedLayout } = useSelectedLayout();

    const recordingDevices = getDevicesWithRecording(devices, type);

    const [selectionMode, setSelectionMode] = useState<'all' | 'select'>('all');

    // Инициализируем выбранные устройства при открытии
    useEffect(() => {
        if (visible) {
            setSelectionMode('all');
            setSelectedDevices([...recordingDevices]);
        }
    }, [visible, selectedLayout]);

    const handleOk = () => {
        // Фильтруем только устройства, которые записывают и онлайн
        const onlineRecordingDevices = selectedDevices.filter(
            (device) => recordingDevices.some((d) => d.UID === device.UID) && device.online,
        );

        // Находим offline устройства, которые пытались остановить
        const offlineDevices = selectedDevices.filter(
            (device) => recordingDevices.some((d) => d.UID === device.UID) && !device.online,
        );

        if (onlineRecordingDevices.length === 0) {
            if (offlineDevices.length > 0) {
                const deviceNames = offlineDevices.map((d) => d.name).join(', ');
                message.error(`Нельзя остановить запись: устройства (${deviceNames}) не онлайн`);
            } else {
                message.error('Выберите хотя бы одну камеру из текущей записи');
            }
            return;
        }

        if (offlineDevices.length > 0) {
            const deviceNames = offlineDevices.map((d) => d.name).join(', ');
            Modal.confirm({
                title: 'Некоторые устройства не онлайн',
                content: `Устройства (${deviceNames}) в данный момент недоступны. Остановить запись только для онлайн устройств?`,
                okText: 'Да, остановить',
                cancelText: 'Отмена',
                okButtonProps: {
                    className: 'button-base button-type-primary button-size-medium',
                },
                cancelButtonProps: {
                    className: 'button-base button-type-secondary button-size-medium',
                },
                className: 'stop-recording-modal-ok',
                onOk: () => {
                    stopRecording(type, onlineRecordingDevices);
                    setIsModalVisible(false);
                    onOk?.(onlineRecordingDevices);
                },
            });
        } else {
            stopRecording(type, onlineRecordingDevices);
            setIsModalVisible(false);
            onOk?.(onlineRecordingDevices);
        }
    };

    // const handleOk = () => {
    //     // Убедимся, что останавливаем только устройства текущей раскладки
    //     const devicesToStop = selectedDevices.filter((device) =>
    //         recordingDevices.some((d) => d.UID === device.UID),
    //     );
    //
    //     if (devicesToStop.length === 0) {
    //         message.error('Выберите хотя бы одну камеру из текущей записи');
    //         return;
    //     }
    //
    //     const onlineDevices = devicesToStop.filter((device) => device.online);
    //
    //     if (devicesToStop.length !== onlineDevices.length) {
    //         message.error(
    //             'В выбранных камерах присутствует камера, которая не онлайн. Остановить запись невозможно.',
    //         );
    //         return;
    //     }
    //
    //     console.log(onlineDevices);
    //
    //     // Останавливаем запись только для выбранных устройств текущей раскладки
    //     stopRecording(type, devicesToStop);
    //     setIsModalVisible(false);
    //     onOk?.(devicesToStop);
    // };

    const handleSelectionModeChange = (value: 'all' | 'select') => {
        setSelectionMode(value);
        if (value === 'all') {
            // При выборе "со всех" автоматически выбираем все доступные
            setSelectedDevices([...recordingDevices]);
        } else {
            // При ручном выборе сбрасываем выбор
            setSelectedDevices([]);
        }
    };

    return (
        <Modal
            title={
                <div className="modal_header headline small">
                    <span className="modal-title">{title}</span>
                    {<SvgClose className="custom-close-icon" onClick={onCancel} />}
                </div>
            }
            visible={visible}
            onOk={handleOk}
            onCancel={onCancel}
            className={'stop-recording-modal'}
            footer={[
                <Button
                    key="ok"
                    className="button-base button-type-primary button-size-medium "
                    onClick={handleOk}
                >
                    Остановить запись
                </Button>,
            ]}
        >
            <SelectChecker
                label={'Выберите уст-ва:'}
                value={selectionMode}
                overlayClassName={'record-modal-select-checker-overlay'}
                labelClassName={'record-modal-select-checker-label title medium'}
                onChange={(e) => handleSelectionModeChange(e.target.value)}
                options={[
                    { label: 'со всех', value: 'all' },
                    { label: 'выбрать с каких', value: 'select' },
                ]}
            />
            <ModalCameraGrid
                devices={recordingDevices}
                selectedDevices={selectedDevices}
                onTileClick={(device) => {
                    if (selectionMode === 'select') {
                        const isSelected = selectedDevices.some((d) => d.UID === device.UID);
                        setSelectedDevices(
                            isSelected
                                ? selectedDevices.filter((d) => d.UID !== device.UID)
                                : [...selectedDevices, device],
                        );
                    }
                }}
                getTileStatus={() => ({
                    highlight: type,
                    tooltip: `Идет запись ${type}`,
                })}
            />
        </Modal>
    );
};
